import { type AssignmentImpact, type ContactList, type DirectoryEntry, PRICING } from "@kontaktfix/shared";
import {
  Avatar,
  Button,
  Card,
  CardHeader,
  Checkbox,
  Dialog,
  EmptyState,
  Input,
  Menu,
  MenuItem,
  Skeleton,
  Tabs,
  Tooltip,
} from "@kontaktfix/ui";
import {
  ArrowLeftIcon,
  ArrowsClockwiseIcon,
  DotsThreeIcon,
  MagnifyingGlassIcon,
  PencilSimpleIcon,
  PlusIcon,
  TrashIcon,
  UserIcon,
  UserMinusIcon,
  UsersIcon,
  WarningIcon,
} from "@phosphor-icons/react";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ContactSheet } from "@/components/contact-sheet";
import {
  ContactsTable,
  noSelection,
  Pagination,
  SelectAllBanner,
  type Selection,
  selectionBody,
  selectionCount,
} from "@/components/contacts-table";
import { DirectoryPicker } from "@/components/directory-picker";
import { fileReason } from "@/components/file-source";
import { EntityHistory } from "@/components/history";
import { LIST_COLOR_HEX } from "@/components/list-badge";
import { Page } from "@/components/page";
import { api } from "@/lib/api";
import { toastError } from "@/lib/errors";
import { formatMoney, formatNumber, formatRelative } from "@/lib/i18n";
import {
  useAssignments,
  useCanChangeContacts,
  useContacts,
  useInvalidateData,
  useList,
  useLists,
  useMe,
  useMembers,
} from "@/lib/queries";
import { ExportMenu, useDebounced } from "./contacts";
import { ListFormDialog } from "./lists";

const PAGE_SIZE = 50;

function ContactsTab({ list }: { list: ContactList }) {
  const { t } = useTranslation();
  const navigate = useNavigate({ from: "/lists/$listId" });
  const [query, setQuery] = useState("");
  const q = useDebounced(query);
  const [page, setPage] = useState(1);
  const { data, isLoading } = useContacts({ listId: list.id, q, page, pageSize: PAGE_SIZE });
  const { data: lists = [] } = useLists();
  const invalidate = useInvalidateData();
  const [selected, setSelected] = useState<Selection>(noSelection);
  const total = data?.total ?? 0;
  const count = selectionCount(selected, total);
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset on search change only
  useEffect(() => {
    setPage(1);
    setSelected((s) => (s.all ? noSelection : s));
  }, [q]);
  const [adding, setAdding] = useState(false);
  // Read-only once the trial ended without a subscription.
  const canChange = useCanChangeContacts();
  // Directory and file lists fill themselves: nothing to add, remove or import here.
  const canEdit = list.canEdit && canChange && list.kind === "manual";

  const remove = async () => {
    try {
      const { changed } = await api.post<{ changed: number }>("/api/contacts/bulk/remove-list", {
        ...selectionBody(selected, { q, listId: list.id }, total),
        listId: list.id,
      });
      toast.success(t("contacts.removedFromList", { count: changed, n: formatNumber(changed) }));
      setSelected(noSelection);
      await invalidate();
    } catch (err) {
      toastError(err);
      await invalidate();
    }
  };

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Input
          icon={<MagnifyingGlassIcon />}
          className="max-w-sm flex-1"
          placeholder={t("contacts.search")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="ml-auto flex gap-2">
          {count > 0 && canEdit && (
            <Button variant="danger-ghost" onClick={remove}>
              <UserMinusIcon />
              {t("contacts.removeFromList")} ({formatNumber(count)})
            </Button>
          )}
          {canEdit && (
            <>
              <Button onClick={() => setAdding(true)}>
                <PlusIcon />
                {t("lists.addContacts")}
              </Button>
              <Button
                variant="primary"
                onClick={() => navigate({ search: (s) => ({ ...s, contact: "new" }) })}
              >
                <PlusIcon />
                {t("contacts.new")}
              </Button>
            </>
          )}
        </div>
      </div>
      <Card>
        {!isLoading && !data?.items.length ? (
          <EmptyState icon={<UsersIcon />} title={q ? t("contacts.noResults") : t("contacts.empty")} />
        ) : (
          <>
            {canEdit && (
              <SelectAllBanner
                items={data?.items ?? []}
                total={total}
                selected={selected}
                onSelectedChange={setSelected}
              />
            )}
            <ContactsTable
              items={data?.items ?? []}
              lists={lists}
              loading={isLoading}
              selected={selected}
              onSelectedChange={setSelected}
              selectable={canEdit}
              onOpen={(id) => navigate({ search: (s) => ({ ...s, contact: id }) })}
            />
            <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPage={setPage} />
          </>
        )}
      </Card>
      <AddContactsDialog list={list} open={adding} onOpenChange={setAdding} />
    </>
  );
}

const ADD_PAGE_SIZE = 50;

function AddContactsDialog({
  list,
  open,
  onOpenChange,
}: {
  list: ContactList;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const q = useDebounced(query);
  const [page, setPage] = useState(1);
  // The server leaves out contacts already on the list, so every page is full and the total is right.
  const { data } = useContacts({ q, notInListId: list.id, page, pageSize: ADD_PAGE_SIZE });
  const invalidate = useInvalidateData();
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const candidates = data?.items ?? [];

  const add = async () => {
    setBusy(true);
    try {
      const { changed } = await api.post<{ changed: number }>("/api/contacts/bulk/add-list", {
        contactIds: [...picked],
        listId: list.id,
      });
      toast.success(t("contacts.addedToList", { count: changed, n: formatNumber(changed) }));
      setPicked(new Set());
      onOpenChange(false);
      await invalidate();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("lists.addContactsTitle", { name: list.name })}
      className="max-w-lg"
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>{t("common.cancel")}</Button>
          <Button variant="primary" disabled={!picked.size} loading={busy} onClick={add}>
            {t("common.add")}
            {picked.size ? ` (${picked.size})` : ""}
          </Button>
        </>
      }
    >
      <Input
        icon={<MagnifyingGlassIcon />}
        autoFocus
        placeholder={t("contacts.search")}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setPage(1);
        }}
      />
      <div className="mt-3 flex max-h-80 flex-col gap-0.5 overflow-y-auto">
        {candidates.map((c) => (
          <label
            key={c.id}
            className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 hover:bg-muted"
          >
            <Checkbox
              checked={picked.has(c.id)}
              onCheckedChange={() =>
                setPicked((p) => {
                  const next = new Set(p);
                  if (next.has(c.id)) next.delete(c.id);
                  else next.add(c.id);
                  return next;
                })
              }
              label={c.displayName}
            />
            <Avatar name={c.displayName} size={26} />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13px] font-medium">{c.displayName}</span>
              <span className="block truncate text-[12px] text-fg-muted">{c.companyName}</span>
            </span>
          </label>
        ))}
        {!candidates.length && (
          <p className="py-6 text-center text-[13px] text-fg-muted">{t("contacts.noResults")}</p>
        )}
      </div>
      <div className="mt-2">
        <Pagination page={page} pageSize={ADD_PAGE_SIZE} total={data?.total ?? 0} onPage={setPage} />
      </div>
    </Dialog>
  );
}

/** Before a large assignment: how many contacts each person gets and how long the first delivery takes. */
/** Directory lists: where the employees come from, the last refresh, and who is left out and why. */
function DirectoryStatus({ directory }: { directory: NonNullable<ContactList["directory"]> }) {
  const { t } = useTranslation();
  const [showExcluded, setShowExcluded] = useState(false);
  return (
    <div className="mb-4 rounded-md border border-border bg-subtle px-3 py-2 text-[12.5px] text-fg-muted">
      <p>
        {t("lists.directory.status", {
          when: directory.refreshedAt ? formatRelative(directory.refreshedAt) : t("lists.directory.pending"),
        })}
      </p>
      {directory.error && (
        <p className="mt-1 text-danger">{t("lists.directory.error", { error: directory.error })}</p>
      )}
      {directory.excluded.length > 0 && (
        <div className="mt-1">
          <button
            type="button"
            className="text-accent hover:underline"
            onClick={() => setShowExcluded(!showExcluded)}
          >
            {t("lists.directory.excludedCount", { count: directory.excluded.length })}
          </button>
          {showExcluded && (
            <ul className="mt-2 flex flex-col gap-1">
              {directory.excluded.map((e) => (
                <li key={e.id}>
                  <span className="text-fg">{e.displayName}</span> ·{" "}
                  {t(`lists.directory.reasons.${e.reason}`)}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

/** File lists (issue #28): last fetch, what changed, problems, and fetching now. */
function FileStatus({ list, isAdmin }: { list: ContactList; isAdmin: boolean }) {
  const { t } = useTranslation();
  const invalidate = useInvalidateData();
  const [fetching, setFetching] = useState(false);
  const file = list.file!;
  const fetchNow = async () => {
    setFetching(true);
    try {
      const updated = await api.post<ContactList>(`/api/lists/${list.id}/refresh`);
      if (updated.file?.error) toast.error(fileReason(t, updated.file.error));
      else toast.success(t("lists.file.fetched"));
      await invalidate();
    } catch (err) {
      toastError(err);
    } finally {
      setFetching(false);
    }
  };
  return (
    <div className="mb-4 flex flex-wrap items-start justify-between gap-3 rounded-md border border-border bg-subtle px-3 py-2 text-[12.5px] text-fg-muted">
      <div className="min-w-0">
        <p>
          {t("lists.file.status", {
            when: file.refreshedAt ? formatRelative(file.refreshedAt) : t("lists.file.pending"),
            schedule: t(`lists.file.schedules.${file.schedule}`),
          })}
        </p>
        <p className="truncate font-mono text-[11.5px]">{file.url}</p>
        {file.stats && !file.error && (
          <p className="mt-1">
            {t("lists.file.stats", {
              rows: formatNumber(file.stats.rows),
              created: formatNumber(file.stats.created),
              updated: formatNumber(file.stats.updated),
              removed: formatNumber(file.stats.removed),
            })}
            {file.stats.missing > 0 &&
              ` ${t("lists.file.missing", { count: file.stats.missing, days: file.deletionDays })}`}
            {file.stats.invalid + file.stats.duplicates > 0 &&
              ` ${t("lists.file.skipped", { count: file.stats.invalid + file.stats.duplicates })}`}
          </p>
        )}
        {file.error && (
          <p className="mt-1 text-danger">{t("lists.file.error", { error: fileReason(t, file.error) })}</p>
        )}
      </div>
      {isAdmin && (
        <Button size="sm" onClick={fetchNow} loading={fetching}>
          <ArrowsClockwiseIcon />
          {t("lists.file.fetchNow")}
        </Button>
      )}
    </div>
  );
}

function AssignmentImpactDialog({
  name,
  impact,
  saving,
  onCancel,
  onConfirm,
}: {
  name: string;
  impact: AssignmentImpact;
  saving: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  const over = impact.maxContactsPerRecipient > impact.limits.perRecipient;
  const minutes = (n: number) =>
    n >= 120
      ? t("lists.impact.hours", { count: Math.round(n / 60) })
      : t("lists.impact.minutes", { count: n });
  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onCancel()}
      title={t("lists.impact.title", { name })}
      footer={
        <>
          <Button onClick={onCancel}>{t("common.cancel")}</Button>
          {!over && (
            <Button variant="primary" loading={saving} onClick={onConfirm}>
              {t("lists.assignments.add")}
            </Button>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-3 text-[13px] leading-relaxed">
        <p>
          {t("lists.impact.summary", {
            contacts: formatNumber(impact.maxContactsPerRecipient),
            count: impact.recipients,
            people: formatNumber(impact.recipients),
          })}
        </p>
        {impact.newRecipients > 0 && (
          <p>
            {t("lists.impact.seats", {
              count: impact.newRecipients,
              people: formatNumber(impact.newRecipients),
              amount: formatMoney(impact.newRecipients * PRICING.pricePerUser, PRICING.currency),
            })}
          </p>
        )}
        {over ? (
          <p className="rounded-md bg-danger-soft px-3 py-2 text-danger">
            {t("lists.impact.overLimit", { limit: formatNumber(impact.limits.perRecipient) })}
          </p>
        ) : (
          <>
            <p>
              {t("lists.impact.duration", {
                from: minutes(impact.estimate.minMinutes),
                to: minutes(impact.estimate.maxMinutes),
              })}
            </p>
            <p className="text-fg-muted">{t("lists.impact.phone")}</p>
            {impact.maxContactsPerRecipient > impact.limits.perRecipientWarning && (
              <p className="rounded-md bg-warning-soft px-3 py-2">{t("lists.impact.warning")}</p>
            )}
          </>
        )}
      </div>
    </Dialog>
  );
}

function AssignmentsTab({ list, isAdmin }: { list: ContactList; isAdmin: boolean }) {
  const { t } = useTranslation();
  const { data: assignments, isLoading } = useAssignments(list.id);
  const invalidate = useInvalidateData();
  const [picking, setPicking] = useState(false);
  /** A large assignment waits for confirmation with the estimate (issue #33). */
  const [pending, setPending] = useState<{ entry: DirectoryEntry; impact: AssignmentImpact } | null>(null);
  const [saving, setSaving] = useState(false);

  const save = async (entry: DirectoryEntry) => {
    setSaving(true);
    try {
      await api.post(`/api/lists/${list.id}/assignments`, { targetType: entry.type, targetId: entry.id });
      toast.success(t("lists.assignments.added"));
      setPicking(false);
      setPending(null);
      await invalidate();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };
  const add = async (entry: DirectoryEntry) => {
    try {
      const impact = await api.post<AssignmentImpact>(`/api/lists/${list.id}/assignments/impact`, {
        targetType: entry.type,
        targetId: entry.id,
      });
      const large =
        impact.maxContactsPerRecipient > impact.limits.perRecipientWarning ||
        impact.estimate.maxMinutes > 5 ||
        // Many new people (e.g. "all employees") change the bill: show it before saving.
        impact.newRecipients >= 10;
      if (large) {
        setPicking(false);
        setPending({ entry, impact });
      } else await save(entry);
    } catch (err) {
      toastError(err);
    }
  };
  const remove = async (id: string) => {
    try {
      await api.delete(`/api/assignments/${id}`);
      toast.success(t("lists.assignments.removed"));
      await invalidate();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <Card>
      <CardHeader
        title={t("lists.assignments.title")}
        description={t("lists.assignments.text")}
        actions={
          isAdmin && (
            <Button variant="primary" onClick={() => setPicking(true)}>
              <PlusIcon />
              {t("lists.assignments.add")}
            </Button>
          )
        }
      />
      {isLoading ? (
        <div className="p-4">
          <Skeleton className="h-12" />
        </div>
      ) : !assignments?.length ? (
        <EmptyState
          icon={<UsersIcon />}
          title={t("lists.assignments.empty")}
          description={t("lists.assignments.emptyText")}
        />
      ) : (
        <div className="divide-y divide-border">
          {assignments.map((a) => (
            <div key={a.id} className="flex items-center gap-3 px-5 py-3">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-md border border-border bg-subtle text-fg-muted">
                {a.targetType === "group" ? (
                  <UsersIcon className="size-4" />
                ) : (
                  <UserIcon className="size-4" />
                )}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-[13px] font-medium">
                  {a.displayName}
                  {a.missing && (
                    <Tooltip content={t("lists.assignments.missing")}>
                      <WarningIcon className="size-3.5 text-warning" />
                    </Tooltip>
                  )}
                </div>
                <div className="truncate text-[12px] text-fg-muted">
                  {a.targetType === "group"
                    ? [
                        t("lists.assignments.group"),
                        a.memberCount !== null
                          ? t("lists.assignments.members", { count: a.memberCount })
                          : null,
                        a.detail,
                      ]
                        .filter(Boolean)
                        .join(" · ")
                    : [t("lists.assignments.user"), a.detail].filter(Boolean).join(" · ")}
                </div>
              </div>
              {isAdmin && (
                <Button size="sm" variant="ghost" onClick={() => remove(a.id)}>
                  {t("common.remove")}
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
      <DirectoryPicker
        open={picking}
        onOpenChange={setPicking}
        title={t("lists.assignments.add")}
        excluded={assignments?.map((a) => a.targetId) ?? []}
        onPick={(entry) => add(entry)}
      />
      {pending && (
        <AssignmentImpactDialog
          name={pending.entry.displayName}
          impact={pending.impact}
          saving={saving}
          onCancel={() => setPending(null)}
          onConfirm={() => save(pending.entry)}
        />
      )}
    </Card>
  );
}

function EditorsTab({ list, isAdmin }: { list: ContactList; isAdmin: boolean }) {
  const { t } = useTranslation();
  const { data: members = [] } = useMembers();
  const invalidate = useInvalidateData();
  const editors = members.filter((m) => list.editorIds.includes(m.id));
  const candidates = members.filter((m) => m.role === "editor");

  const toggle = async (memberId: string) => {
    const next = list.editorIds.includes(memberId)
      ? list.editorIds.filter((id) => id !== memberId)
      : [...list.editorIds, memberId];
    try {
      await api.put(`/api/lists/${list.id}/editors`, { memberIds: next });
      toast.success(t("lists.editors.saved"));
      await invalidate();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <Card>
      <CardHeader
        title={t("lists.editors.title")}
        description={t("lists.editors.text")}
        actions={
          isAdmin &&
          candidates.length > 0 && (
            <Menu
              trigger={
                <Button>
                  <PencilSimpleIcon />
                  {t("lists.editors.manage")}
                </Button>
              }
            >
              {candidates.map((m) => (
                <MenuItem key={m.id} onSelect={() => toggle(m.id)}>
                  <span className="w-4">{list.editorIds.includes(m.id) ? "✓" : ""}</span>
                  {m.name}
                </MenuItem>
              ))}
            </Menu>
          )
        }
      />
      {!editors.length ? (
        <p className="px-5 py-6 text-[13px] text-fg-muted">
          {t("lists.editors.none")} {isAdmin && !candidates.length && t("lists.editors.noMembers")}
        </p>
      ) : (
        <div className="divide-y divide-border">
          {editors.map((m) => (
            <div key={m.id} className="flex items-center gap-3 px-5 py-3">
              <Avatar name={m.name} size={30} />
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-medium">{m.name}</div>
                <div className="text-[12px] text-fg-muted">{m.email}</div>
              </div>
              {isAdmin && (
                <Button size="sm" variant="ghost" onClick={() => toggle(m.id)}>
                  {t("common.remove")}
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export function ListDetailPage() {
  const { t } = useTranslation();
  const { listId } = useParams({ from: "/app/lists/$listId" });
  const search = useSearch({ from: "/app/lists/$listId" });
  const navigate = useNavigate({ from: "/lists/$listId" });
  const { data: me } = useMe();
  const { data: list, isLoading } = useList(listId);
  const invalidate = useInvalidateData();
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const isAdmin = me?.role === "admin";
  const tab = search.tab ?? "contacts";

  if (isLoading || !list) {
    return (
      <Page>
        <Skeleton className="mb-4 h-8 w-64" />
        <Skeleton className="h-64" />
      </Page>
    );
  }

  const remove = async () => {
    try {
      await api.delete(`/api/lists/${list.id}`);
      toast.success(t("lists.deleted"));
      await invalidate();
      await navigate({ to: "/lists" });
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <Page wide>
      <Link
        to="/lists"
        className="mb-4 inline-flex items-center gap-1.5 text-[12.5px] text-fg-muted hover:text-fg"
      >
        <ArrowLeftIcon className="size-3.5" />
        {t("lists.title")}
      </Link>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <span
            className="mt-0.5 flex size-10 shrink-0 items-center justify-center rounded-lg"
            style={{ background: `${LIST_COLOR_HEX[list.color]}1f`, color: LIST_COLOR_HEX[list.color] }}
          >
            <UsersIcon className="size-5" />
          </span>
          <div>
            <h1 className="text-[22px] font-semibold tracking-tight">{list.name}</h1>
            <p className="mt-0.5 text-[13px] text-fg-muted">
              {[
                list.description,
                `${list.contactCount} ${t("lists.columns.contacts")}`,
                t("lists.recipientsCount", { count: list.recipientCount }),
              ]
                .filter(Boolean)
                .join(" · ")}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <ExportMenu listId={list.id} />
          {isAdmin && (
            <Menu
              trigger={
                <Button size="icon" aria-label={t("common.actions")}>
                  <DotsThreeIcon />
                </Button>
              }
            >
              <MenuItem icon={<PencilSimpleIcon />} onSelect={() => setEditing(true)}>
                {t("common.edit")}
              </MenuItem>
              <MenuItem icon={<TrashIcon />} danger onSelect={() => setConfirmDelete(true)}>
                {t("common.delete")}
              </MenuItem>
            </Menu>
          )}
        </div>
      </div>
      {!list.canEdit && (
        <div className="mb-4 rounded-md border border-border bg-subtle px-3 py-2 text-[12.5px] text-fg-muted">
          {t("lists.readOnly")}
        </div>
      )}
      {list.directory && <DirectoryStatus directory={list.directory} />}
      {list.file && <FileStatus list={list} isAdmin={isAdmin} />}
      <Tabs
        className="mb-5"
        value={tab}
        onValueChange={(v) => navigate({ search: (s) => ({ ...s, tab: v }) })}
        tabs={[
          { value: "contacts", label: t("lists.tabs.contacts"), count: list.contactCount },
          { value: "assignments", label: t("lists.tabs.assignments"), count: list.assignmentCount },
          { value: "editors", label: t("lists.tabs.editors"), count: list.editorIds.length },
          { value: "history", label: t("lists.tabs.history") },
        ]}
      />
      {tab === "contacts" && <ContactsTab list={list} />}
      {tab === "assignments" && <AssignmentsTab list={list} isAdmin={isAdmin} />}
      {tab === "editors" && <EditorsTab list={list} isAdmin={isAdmin} />}
      {tab === "history" && (
        <Card className="px-5 py-2">
          <EntityHistory entityType="list" entityId={list.id} />
        </Card>
      )}

      <ContactSheet
        contactId={search.contact ?? null}
        defaultListIds={[list.id]}
        onClose={() => navigate({ search: (s) => ({ ...s, contact: undefined }) })}
      />
      <ListFormDialog open={editing} onOpenChange={setEditing} list={list} />
      <Dialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={t("lists.deleteTitle", { name: list.name })}
        description={t("lists.deleteText")}
        footer={
          <>
            <Button onClick={() => setConfirmDelete(false)}>{t("common.cancel")}</Button>
            <Button variant="danger" onClick={remove}>
              {t("common.delete")}
            </Button>
          </>
        }
      />
    </Page>
  );
}
